import React, { useState } from 'react'
import {
  H2,
  Button,
  Line,
  Flex,
  MultiInputFile,
  InputModal,
  Close,
  H4,
  P,
} from '../../lib/components'
import { CircularProgress } from '@mui/material'
import './unit-price-form.scss'

interface UnitPriceFormPresenterProps {
  type: string
  handleUnitPriceFiles: (files: File[]) => Promise<void>
  isLoading: boolean
}

const UnitPriceFormPresenter = ({
  type,
  handleUnitPriceFiles,
  isLoading,
}: UnitPriceFormPresenterProps) => {
  const [displayUploadModal, setDisplayUploadModal] = useState(false)
  const [unitPriceFiles, setUnitPriceFiles] = useState<File[]>([])

  const downloadTemplateCSV = () => {
    // Create CSV content
    if (type === 'general') {
      const csvContent =
        'opportunity_id,price,date\n' +
        'opportunity-123,1050,2024-12-31\n' +
        'opportunity-456,2575,2024-12-31'

      // Create a blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.setAttribute('hidden', '')
      a.setAttribute('href', url)
      a.setAttribute('download', 'general_unit_price_template.csv')
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } else {
      const csvContent =
        'opportunity_id,price,date,investor_email\n' +
        'opportunity-123,1050,2024-12-31,investor1@gmail.com\n' +
        'opportunity-456,2575,2024-12-31,investor2@gmail.com'

      // Create a blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.setAttribute('hidden', '')
      a.setAttribute('href', url)
      a.setAttribute('download', 'investor_specific_unit_price_template.csv')
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }

  return (
    <Flex variant='column-center' classes={['unit-price-form-container']}>
      {/* <H2 classes={['mb-48']} text='Update Unit Prices' /> */}
      {type === 'general' ? (
        <>
          <H2 classes={['mb-48']} text='Update General Unit Prices' />
          <div
            className='instructions-container'
            data-testid='unit-price-form-instructions-container'
          >
            <P classes={['mb-16']} text='Instructions for updating unit prices:' />
            <ul>
              <li className='instruction-item'>
                Prepare a CSV file with the following columns: opportunity_id, price, date
              </li>
              <li className='instruction-item'>
                Get the opportunity_id from the opportunity list sheet
              </li>
              <li className='instruction-item'>Unit price should be in cents (e.g., 1050)</li>
              <li className='instruction-item'>Date should be in YYYY-MM-DD format</li>
              <li className='instruction-item'>
                Each row will update the unit price for one opportunity
              </li>
            </ul>
            <span className='csv-template-link' onClick={downloadTemplateCSV}>
              Download CSV Template
            </span>
          </div>
        </>
      ) : (
        <>
          <H2 classes={['mb-48']} text='Update Client Unit Prices' />
          <div
            className='instructions-container'
            data-testid='unit-price-form-instructions-container'
          >
            <P classes={['mb-16']} text='Instructions for updating client specific unit prices:' />
            <ul>
              <li className='instruction-item'>
                Prepare a CSV file with the following columns: opportunity_id, price, date,
                investor_email
              </li>
              <li className='instruction-item'>
                Get the opportunity_id from the opportunity list sheet
              </li>
              <li className='instruction-item'>Unit price should be in cents (e.g., 1050)</li>
              <li className='instruction-item'>Date should be in YYYY-MM-DD format</li>
              <li className='instruction-item'>
                Get the investor_email from the client list sheet
              </li>
              <li className='instruction-item'>
                Each row will update the unit price for one opportunity
              </li>
            </ul>
            <span className='csv-template-link' onClick={downloadTemplateCSV}>
              Download CSV Template
            </span>
          </div>
        </>
      )}

      {/* <div className='instructions-container' data-testid='unit-price-form-instructions-container'>
        <P classes={['mb-16']} text='Instructions for updating unit prices:' />
        <ul>
          <li className='instruction-item'>
            Prepare a CSV file with the following columns: opportunity_id, price, date
          </li>
          <li className='instruction-item'>
            Get the opportunity_id from the opportunity list sheet
          </li>
          <li className='instruction-item'>Unit price should be in cents (e.g., 1050)</li>
          <li className='instruction-item'>Date should be in YYYY-MM-DD format</li>
          <li className='instruction-item'>
            Each row will update the unit price for one opportunity
          </li>
        </ul>
        <span className='csv-template-link' onClick={downloadTemplateCSV}>
          Download CSV Template
        </span>
      </div> */}

      <Flex classes={['upload-csv-container']}>
        {displayUploadModal ? (
          <InputModal>
            <Flex
              variant='column'
              classes={['input-modal']}
              dataTestId='unit-price-upload-csv-modal'
            >
              <Flex variant='space-between'>
                <H4 text={'Upload Unit Prices'} />
                <Close
                  height='24px'
                  onClick={() => {
                    if (!isLoading) {
                      setDisplayUploadModal(false)
                      setUnitPriceFiles([])
                    }
                  }}
                />
              </Flex>
              <Flex variant='column' classes={['reply-section']}>
                <Line />
                <MultiInputFile
                  name={'unitPriceFile'}
                  type='csv'
                  handleFiles={async (files: any) => {
                    if (files && !isLoading) {
                      setUnitPriceFiles(files)
                    }
                  }}
                  label='Upload CSV file with unit prices'
                  dataTestId='unit-price-upload-csv-file'
                />
                <Line />
                <Flex variant='row' classes={['mt-24', 'align-center']}>
                  <Button
                    classes={isLoading ? ['mr-16'] : []}
                    variant='primary-btn'
                    text='Upload'
                    isDisabled={
                      (unitPriceFiles && unitPriceFiles.length > 0 ? false : true) || isLoading
                    }
                    handleClick={async () => {
                      await handleUnitPriceFiles(unitPriceFiles)
                      setDisplayUploadModal(false)
                      setUnitPriceFiles([])
                    }}
                  />
                  {isLoading && <CircularProgress size={20} />}
                </Flex>
              </Flex>
            </Flex>
          </InputModal>
        ) : null}
      </Flex>
      <Flex variant='column-center'>
        <Button
          classes={['mt-8', 'p-16']}
          variant='primary-btn'
          handleClick={() => {
            if (!isLoading) {
              setDisplayUploadModal(true)
            }
          }}
          text={'Upload Unit Price CSV'}
          isDisabled={isLoading}
        />
      </Flex>
    </Flex>
  )
}

export { UnitPriceFormPresenter }
